import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import { ko } from "date-fns/locale";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";

interface Props extends Omit<ReactDatePickerProps<never, true>, "startDate" | "endDate"> {
  dateRange: [Date | null, Date | null];
  disabled?: boolean;
  type?: "month" | "";
  dateFormat?: "yyyy.MM" | "";
  placeholder?: string;
  className?: string;
  onClearClick?: () => void;
  showClearButton?: boolean;
  readonly?: boolean;
}

function RangeDatepicker(props: Props) {
  const { showClearButton = true } = props;
  // 📅 날짜 범위 상태 관리
  const [rangeDate, setRangeDate] = useState<[Date | null, Date | null]>([null, null]);
  // 📝 input 표시값 관리
  const [inputValue, setInputValue] = useState("");
  // 📅 캘린더 열림/닫힘 상태
  const [calendarOpen, setCalendarOpen] = useState(false);
  // ❌ 날짜 형식 오류 상태
  const [hasDateError, setHasDateError] = useState(false);
  // 🎯 input 포커스 제어용 ref
  const inputRef = useRef<HTMLInputElement>(null);

  // 🔄 props 날짜 변경 시 내부 state 동기화
  useEffect(() => {
    setHasDateError(false);
    setRangeDate(props.dateRange);
    // props에서 날짜가 변경되면 input 값도 업데이트
    if (props.dateRange[0] && props.dateRange[1]) {
      // 📅 두 날짜 모두 있는 경우: "YYYY.MM.DD - YYYY.MM.DD" 형태
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}.${month}.${day}`;
      };
      setInputValue(`${formatDate(props.dateRange[0])} - ${formatDate(props.dateRange[1])}`);
    } else if (props.dateRange[0]) {
      // 📅 시작 날짜만 있는 경우: "YYYY.MM.DD" 형태
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}.${month}.${day}`;
      };
      setInputValue(formatDate(props.dateRange[0]));
    } else {
      // 🗑️ 날짜가 없는 경우: 빈 문자열
      setInputValue("");
    }
  }, [props.dateRange]);

  // ✏️ 실시간 타이핑 시 자동 포맷팅 함수 (YYYY.MM.DD - YYYY.MM.DD)
  const formatInputValue = (value: string) => {
    // ' - ' 구분자 기준으로 앞뒤 분리
    const parts = value.split(" - ");

    if (parts.length === 2) {
      // 📅 범위 입력 중인 경우 (이미 ' - '가 있음)
      const startPart = parts[0].trim();
      const endPart = parts[1].trim();

      // 🔢 단일 날짜 포맷팅 함수
      const formatSingleDate = (dateStr: string) => {
        const numbers = dateStr.replace(/[^\d]/g, "");
        if (numbers.length === 0) return "";
        if (numbers.length <= 4) {
          return numbers;
        } else if (numbers.length <= 6) {
          return `${numbers.slice(0, 4)}.${numbers.slice(4)}`;
        } else {
          return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)}`;
        }
      };

      const formattedStart = formatSingleDate(startPart);
      const formattedEnd = formatSingleDate(endPart);

      // 🧹 빈 값 처리
      if (formattedStart === "" && formattedEnd === "") {
        return "";
      } else if (formattedStart === "") {
        return ` - ${formattedEnd}`;
      } else if (formattedEnd === "") {
        return `${formattedStart} - `;
      } else {
        return `${formattedStart} - ${formattedEnd}`;
      }
    } else {
      // 📝 단일 날짜 입력 중인 경우 (연속으로 16자리까지 입력 가능)
      const numbers = value.replace(/[^\d]/g, "");

      if (numbers.length === 0) return "";
      if (numbers.length <= 4) {
        return numbers; // YYYY
      } else if (numbers.length <= 6) {
        return `${numbers.slice(0, 4)}.${numbers.slice(4)}`; // YYYY.MM
      } else if (numbers.length <= 8) {
        return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6)}`; // YYYY.MM.DD
      } else if (numbers.length <= 12) {
        // 🔄 8자리 완성 후 ' - ' 자동 추가하고 두 번째 날짜 시작
        return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)} - ${numbers.slice(8)}`;
      } else if (numbers.length <= 14) {
        return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)} - ${numbers.slice(8, 12)}.${numbers.slice(12)}`;
      } else {
        // 📅 최대 16자리까지 지원 (YYYY.MM.DD - YYYY.MM.DD)
        return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)} - ${numbers.slice(8, 12)}.${numbers.slice(
          12,
          14,
        )}.${numbers.slice(14, 16)}`;
      }
    }
  };

  // 📅 moment를 사용한 간단한 날짜 파싱
  const parseDate = (dateStr: string): Date | null => {
    if (dateStr && dateStr.length === 10) {
      const momentDate = moment(dateStr, "YYYY.MM.DD", true);
      return momentDate.isValid() ? momentDate.toDate() : null;
    }
    return null;
  };

  // ⌨️ input 입력 변경 처리 함수
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    // ⬅️ 백스페이스로 값을 지우는 경우 (포맷팅 없이 그대로 적용)
    if (value.length < inputValue.length) {
      setInputValue(value);
      setHasDateError(false); // 🧹 지울 때 에러 초기화
      return;
    }

    // ✏️ 자동 포맷팅 적용
    const formattedValue = formatInputValue(value);
    setInputValue(formattedValue);

    // 🎯 커서 위치 복원 (포맷팅으로 인한 문자 추가 고려)
    setTimeout(() => {
      if (inputRef.current) {
        // 포맷팅으로 인해 문자가 추가된 경우 커서 위치 조정
        const lengthDiff = formattedValue.length - value.length;
        const newCursorPosition = cursorPosition + lengthDiff;
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  // ⌨️ 엔터 키 눌렀을 때 검색 실행 처리
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const parts = inputValue.split(" - ");

      // 🗑️ 빈 값 입력 시 전체 기간 검색
      if (inputValue === "") {
        setRangeDate([null, null]);
        setCalendarOpen(false);
        setHasDateError(false); // 🧹 빈 값일 때 에러 초기화
        if (props.onChange) {
          props.onChange([null, null], e as any);
        }
        return;
      }

      // 📅 날짜 범위 입력된 경우 처리
      if (parts.length === 2) {
        const startDateStr = parts[0].trim();
        const endDateStr = parts[1].trim();

        // ❌ 포맷 및 유효성 체크
        const isValidDateFormat = (dateStr: string) => {
          if (dateStr.length !== 10) return false;
          if (!/^\d{4}\.\d{2}\.\d{2}$/.test(dateStr)) return false;
          const momentDate = moment(dateStr, "YYYY.MM.DD", true);
          return momentDate.isValid();
        };

        // dateRange[0], dateRange[1] 각각 검증
        if (!isValidDateFormat(startDateStr) || !isValidDateFormat(endDateStr)) {
          setHasDateError(true);
          return;
        }

        const startDateParsed = parseDate(startDateStr);
        const endDateParsed = parseDate(endDateStr);

        if (startDateParsed && endDateParsed) {
          // ❌ 종료날짜가 시작날짜보다 이전인 경우
          if (endDateParsed < startDateParsed) {
            setHasDateError(true);
            return;
          }

          const newRangeDate: [Date | null, Date | null] = [startDateParsed, endDateParsed];
          setRangeDate(newRangeDate);

          // 📅 캘린더 닫기
          setCalendarOpen(false);
          setHasDateError(false); // ✅ 정상 처리시 에러 초기화

          // 🔄 상위 컴포넌트에 변경된 날짜 전달
          if (props.onChange) {
            props.onChange(newRangeDate, e as any);
          }
        } else {
          setHasDateError(true);
        }
      }
    }
  };

  // 📅 캘린더에서 날짜 선택 시 input 표시용 문자열 포맷팅
  const formatDates = (dates: [Date | null, Date | null]) => {
    // Date 객체를 YYYY.MM.DD 문자열로 변환
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}.${month}.${day}`;
    };

    if (dates[0] && dates[1]) {
      // 📅 시작일과 종료일 모두 있는 경우: "YYYY.MM.DD - YYYY.MM.DD"
      return `${formatDate(dates[0])} - ${formatDate(dates[1])}`;
    } else if (dates[0]) {
      // 📅 시작일만 있는 경우: "YYYY.MM.DD"
      return formatDate(dates[0]);
    }
    return "";
  };

  // 🚪 React Portal을 사용하여 캘린더를 body에 렌더링
  const CalendarPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return createPortal(<div className="calendar-portal">{children}</div>, document.body);
  };

  return (
    <>
      <div className="range-datepicker-container">
        {/* 📝 커스텀 input (직접 타이핑 + 자동 포맷팅) */}
        <div className={`range-datepicker-input`}>
          <input
            className={calendarOpen ? "range-custom-input" : ""}
            readOnly={props.readonly}
            ref={inputRef}
            value={inputValue || ""}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onClick={() => {
              setCalendarOpen(true);
              // 🎯 클릭 후 바로 포커스 유지
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            disabled={props.disabled}
            placeholder={calendarOpen ? "YYYY.MM.DD - YYYY.MM.DD" : props.placeholder || "기간선택"}
          />

          {/* 날짜 선택 시 삭제 버튼 노출 */}
          {props.onClearClick && showClearButton && rangeDate[0] && rangeDate[1] && (
            <button
              type="button"
              className="base-clear-btn"
              onClick={() => {
                props.onClearClick?.();
              }}
            ></button>
          )}

          {/* 📅 캘린더 아이콘 */}
          <div
            className="icon"
            onClick={() => {
              if (props.disabled) return;
              setCalendarOpen(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          />
        </div>
        {hasDateError && <p className="validation-text position-absolute pt1 z-index-1">올바른 날짜를 입력해주세요.</p>}

        {/* 🔍 숨겨진 react-datepicker (실제 날짜 선택 로직 담당) */}
        <ReactDatePicker
          {...props}
          selectsRange={true}
          calendarClassName="base-datepicker"
          dateFormat={props.dateFormat ? props.dateFormat : "yyyy.MM.dd"}
          locale={ko}
          startDate={rangeDate[0]}
          endDate={rangeDate[1]}
          open={calendarOpen}
          openToDate={rangeDate[0] || rangeDate[1] || (!rangeDate[0] && !rangeDate[1] ? new Date() : undefined)} // 캘린더 열 때 현재 선택된 날짜로 이동, 아무것도 선택되지 않았을 때만 오늘 날짜
          customInput={<div style={{ display: "none" }} />} // input 숨김
          showMonthYearPicker={props.type === "month" ? true : false}
          popperContainer={CalendarPortal} // Portal로 body에 렌더링
          // popperPlacement="bottom-start"
          onChange={(dates) => {
            setRangeDate(dates);
            const formatted = formatDates(dates);
            setInputValue(formatted);
            setHasDateError(false); // 🧹 캘린더 선택 시 에러 초기화

            // ✅ 날짜 범위가 완성되면 캘린더 자동 닫기
            if (dates[0] && dates[1]) {
              setCalendarOpen(false);
              // 캘린더 닫힌 후 input에 다시 포커스
              setTimeout(() => inputRef.current?.focus(), 100);
            }

            // 🔄 상위 컴포넌트에 변경 알림
            if (props.onChange) {
              props.onChange(dates, {} as any);
            }
          }}
          onClickOutside={() => {
            setCalendarOpen(false);
            // 📅 캘린더 닫혔을 때도 input 포커스 유지
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
        />
        {/* ❌ 날짜 오류 시 에러 메시지 표시 */}
      </div>
    </>
  );
}
export default RangeDatepicker;
